import { useDebounceFn } from '@vueuse/core'
import axios from 'axios'
import { API_URL, getAuthHeader } from '@/services/api.service.js'
import { decompressJson, decompressPeople, decompressBufferFflate } from '@/utils/Utils.js'
import { ref } from 'vue'
import { keyBy } from 'lodash-es'
import dayjs from '@/lib/dayjs'
import { PPTLayoutOptions } from '@/lib/PPTExport'
import chroma from 'chroma-js'
import { logger } from '@/utils/logger'
import { formatExportDate } from '@/services/date.service'

/**
 * One board fills 6 cache keys: includeApprovedRoles (2) × collapseOn (`''`, `orgUnit`, `role`).
 * @type {number}
 */
export const MAX_CACHED_HIERARCHIES = 6

/**
 * @typedef {object} CachedHierarchy
 * @property {string} cacheKey
 * @property {string} etag
 * @property {Array} [data]
 */

/** @type {CachedHierarchy[]} */
const hierarchyCache = []

/**
 * @param {string} cacheKey
 * @returns {CachedHierarchy | undefined}
 */
function findCachedHierarchy(cacheKey) {
  return hierarchyCache.find((entry) => entry.cacheKey === cacheKey)
}

/**
 * @param {string} cacheKey
 * @param {{ etag: string, data?: Array }} entry
 * @returns {void}
 */
function cacheHierarchy(cacheKey, entry) {
  const existingIndex = hierarchyCache.findIndex((item) => item.cacheKey === cacheKey)
  if (existingIndex >= 0) {
    hierarchyCache[existingIndex] = { cacheKey, etag: entry.etag, data: entry.data }
    return
  }

  hierarchyCache.push({ cacheKey, etag: entry.etag, data: entry.data })

  // Prevent bloat from switching instances
  if (hierarchyCache.length > MAX_CACHED_HIERARCHIES) {
    hierarchyCache.shift()
  }
}

// Initialize pendingRequests as a Map to track ongoing requests
const pendingRequests = new Map()

export async function getPeople({ boardId }) {
  const ret = await axios.get(`${await API_URL()}/people/${boardId}`, {
    responseType: 'arraybuffer',
    headers: {
      ...(await getAuthHeader()),
      'Content-Type': 'application/octet-stream'
    }
  })

  if (ret.status === 200) {
    const data = decompressPeople(ret.data) || []
    return data

    // const { data, post, terminate } = useWebWorker(DecompressWorker)

    // try {
    //   post({
    //     buffer: ret.data,
    //     isPeople: true
    //   })
    //   await until(data).changed()
    //   return data.value
    // } finally {
    //   terminate()
    // }
  } else throw new Error(ret.statusText)
}

export async function getPeopleFromLevels({ boardId, levels = 3 }) {
  const ret = await axios.get(`${await API_URL()}/people/${boardId}/from-levels/${levels}`, {
    responseType: 'arraybuffer',
    headers: {
      ...(await getAuthHeader()),
      'Content-Type': 'application/octet-stream'
    }
  })

  if (ret.status === 200) {
    const data = decompressPeople(ret.data) || []
    return data
  } else throw new Error(ret.statusText)
}

/**
 * This function throttles the requests to the backend to avoid sending same reuqests multiple times. Requests
 * are identified by the cacheKey which is a combination of the boardId, includeApprovedRoles and collapseOn.
 * Case: BaseHierarchy and initial hierarchy call are requesting the same data even though their hooks are initialised
 * with different queryKey
 * @param {*} param0
 * @returns
 */
export async function getHierarchy({
  boardId,
  includeApprovedRoles = false,
  collapseOn = null,
  forceFresh = false
}) {
  const cacheKey = `${boardId}-${includeApprovedRoles}-${collapseOn}`

  if (!getHierarchy.pendingPromises) {
    getHierarchy.pendingPromises = new Map()
  }

  if (!forceFresh && getHierarchy.pendingPromises.has(cacheKey)) {
    return getHierarchy.pendingPromises.get(cacheKey)
  }

  const promise = (async () => {
    try {
      const headers = {
        ...(await getAuthHeader())
      }

      const cachedHierarchy = findCachedHierarchy(cacheKey)
      if (cachedHierarchy && cachedHierarchy.data && cachedHierarchy.etag) {
        headers['If-None-Match'] = cachedHierarchy.etag
      }

      const ret = await axios.post(
        `${await API_URL()}/people/${boardId}/hierarchy`,
        { data: { includeApprovedRoles, collapseOn, forceFresh } },
        {
          responseType: 'arraybuffer',
          headers,
          validateStatus: function (status) {
            //return true for 2xx and 304
            return status >= 200 && status < 400
          }
        }
      )

      if (ret.status === 200) {
        const data = decompressPeople(ret.data) || []
        cacheHierarchy(cacheKey, { etag: ret.headers.etag, data })
        return data
      }
      if (ret.status === 304) {
        return cachedHierarchy?.data || []
      }
      throw new Error(ret.statusText)
    } catch (error) {
      getHierarchy.pendingPromises.delete(cacheKey)
      throw error
    }
  })()

  getHierarchy.pendingPromises.set(cacheKey, promise)

  try {
    const result = await promise
    return result
  } finally {
    getHierarchy.pendingPromises.delete(cacheKey)
  }
}

export async function getHierarchyWithFilter({
  boardId,
  filters,
  includeApprovedRoles = false,
  collapseOn = null
}) {
  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/filter-hierarchy`,
    { data: { filters, includeApprovedRoles, collapseOn } },

    {
      responseType: 'arraybuffer',
      headers: {
        ...(await getAuthHeader())
      }
    }
  )

  if (ret.status === 200) {
    const data = decompressPeople(ret.data) || []
    return data
  } else throw new Error(ret.statusText)
}

const hierarchyWithFilterDebounced = useDebounceFn(
  async ({ boardId, filters, includeApprovedRoles = false, collapseOn = null, signal = null }) => {
    const ret = await axios.post(
      `${await API_URL()}/people/${boardId}/filter-hierarchy`,
      { data: { filters, includeApprovedRoles, collapseOn } },
      {
        responseType: 'arraybuffer',
        headers: {
          ...(await getAuthHeader())
        },
        signal
      }
    )

    if (ret.status === 200) {
      const data = decompressPeople(ret.data) || []
      return data
    } else throw new Error(ret.statusText)
  },
  500
)

export async function getHierarchyWithFilterDebounced({
  boardId,
  filters,
  includeApprovedRoles = false,
  collapseOn = null,
  signal = null
}) {
  const response = await hierarchyWithFilterDebounced({
    boardId,
    filters,
    includeApprovedRoles,
    collapseOn,
    signal
  })

  return response || null
}

/**
 * Retrieves owners by querying for a search value and access level within a specific board.
 *
 * @param {Object} options - The options for the query.
 * @param {string} options.boardId - The ID of the board.
 * @param {string} options.search - The search value.
 * @returns {Promise<Object[]>} - A Promise that resolves to an array of owner objects matching the query.
 */
export async function getOwnersByQuery({ boardId, search }) {
  const url = new URL(`${await API_URL()}/people/${boardId}/owners/search`)

  if (search) {
    url.searchParams.set('value', encodeURIComponent(search))
  }

  const ret = await axios.get(url, {
    headers: { ...(await getAuthHeader()) }
  })

  if (ret.status === 200) {
    return ret.data
  } else throw new Error(ret.statusText)
}

/**
 * Retrieves owners and their employee profile
 *
 * @param {Object} options - The options for the query.
 * @param {string} options.boardId - The ID of the board.
 * @returns {Promise<Object[]>} - A Promise that resolves to a map of uid -> { user, personId }
 */
export async function getOwnersProfiles({ boardId, uids }) {
  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/owners/profiles`,
    {
      data: { uids }
    },
    {
      headers: { ...(await getAuthHeader()) }
    }
  )

  if (ret.status === 200) {
    return ret.data
  } else throw new Error(ret.statusText)
}

// export async function getPerson({ boardId, personId }) {
//   try {
//     const ret = await axios.get(`${await API_URL()}/people/${boardId}/${personId}`, {
//       headers: { ...(await getAuthHeader()) }
//     })
//
//     console.log(`getPerson: ${ret.status}`)
//
//     if (ret.status === 200) {
//       return ret.data
//     } else throw new Error(ret.statusText)
//   } catch (error) {
//     console.log(error)
//     return null
//   }
// }

/**
 * In firestore the return value could be a collection of people (emails were not unique)
 * @param boardId
 * @param email
 * @returns {Promise<void>}
 */
export async function getPersonByEmail({ boardId, email }) {
  //POST /people/:boardId/person
  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/person`,
    {
      data: { email }
    },
    {
      headers: { ...(await getAuthHeader()) }
    }
  )

  if (ret.status === 200) {
    return ret.data
  } else throw new Error(ret.statusText)
}

export async function getPeopleByEmail({ boardId, email }) {
  //POST /people/:boardId/email
  try {
    const ret = await axios.post(
      `${await API_URL()}/people/${boardId}/email`,
      {
        data: { email }
      },
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.error(error.message)
    return null
  }
}

export async function exportPpt({
  boardId,
  layers,
  selectedFields,
  filters,
  levelsPerSlide = 2,
  layoutOption = PPTLayoutOptions.HORIZONTAL,
  returnBuffer = false,
  highlightMode,
  outlineHighlightMode,
  themeColors,
  currencySymbol,
  showSubordinateCounts
}) {
  //GET /people/:boardId/export-ppt
  try {
    // Enhance theme colors with calculated values
    const enhancedThemeColors = enhanceThemeColors(themeColors)

    const ret = await axios.post(
      `${await API_URL()}/people/${boardId}/export-ppt`,
      {
        data: {
          selectedFields,
          layers,
          filters,
          levelsPerSlide,
          layoutOption,
          highlightMode,
          outlineHighlightMode,
          themeColors: enhancedThemeColors,
          currencySymbol,
          showSubordinateCounts
        }
      },
      { headers: { ...(await getAuthHeader()) }, responseType: 'blob' }
    )

    if (ret.status !== 200) {
      logger.error('Error exporting ppt', ret.statusText)
      return false
    }

    const responseBlob = new Blob([ret.data])
    const text = await responseBlob.text()

    if (text !== 'error') {
      if (returnBuffer) {
        // Return the buffer for zip file creation
        return ret.data
      } else {
        const url = window.URL.createObjectURL(responseBlob)
        const link = document.createElement('a')
        const timeStamp = dayjs().format('YYYY-MM-DD-HH-mm')
        const fileName = `Export-${timeStamp}.pptx`
        link.href = url
        link.setAttribute('download', fileName)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        return true
      }
    }

    logger.error('Generated ppt file not found')
    return false
  } catch (error) {
    logger.error(error.message)
    throw error
  }
}

/**
 * Queue an async PPT export job for large exports.
 * The export will be processed in the background and the user will receive an email with the download link.
 *
 * @param {Object} params - Export parameters
 * @param {string} params.boardId - Board ID
 * @param {Array} params.layers - Layers to export
 * @param {Array} params.selectedFields - Fields to include
 * @param {Object} params.filters - Filter criteria
 * @param {number} params.levelsPerSlide - Levels per slide
 * @param {string} params.layoutOption - Layout option (horizontal/vertical)
 * @param {string} params.highlightMode - Highlight mode
 * @param {string} params.outlineHighlightMode - Outline highlight mode
 * @param {Object} params.themeColors - Theme colors
 * @param {string} params.currencySymbol - Currency symbol
 * @param {boolean} params.showSubordinateCounts - Whether to show subordinate counts
 * @param {number} params.slideCount - Estimated slide count
 * @returns {Promise<Object>} Job info { jobId, message }
 */
export async function exportPptAsync({
  boardId,
  layers,
  selectedFields,
  filters,
  levelsPerSlide = 2,
  layoutOption = PPTLayoutOptions.HORIZONTAL,
  highlightMode,
  outlineHighlightMode,
  themeColors,
  currencySymbol,
  showSubordinateCounts,
  slideCount
}) {
  try {
    // Enhance theme colors with calculated values
    const enhancedThemeColors = enhanceThemeColors(themeColors)

    const ret = await axios.post(
      `${await API_URL()}/ppt-export-jobs/${boardId}`,
      {
        data: {
          selectedFields,
          layers,
          filters,
          levelsPerSlide,
          layoutOption,
          highlightMode,
          outlineHighlightMode,
          themeColors: enhancedThemeColors,
          currencySymbol,
          showSubordinateCounts,
          slideCount,
          exportDate: formatExportDate()
        }
      },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 202) {
      return ret.data
    }

    logger.error('Error queuing async PPT export', ret.statusText)
    return null
  } catch (error) {
    logger.error(error.message)
    throw error
  }
}

/**
 * Return people matching the emails
 */
export async function getPeopleForEmails({ boardId, emails }) {
  try {
    const ret = await axios.post(
      `${await API_URL()}/people/${boardId}/email/find-all`,
      { data: { emails } },
      { headers: { ...(await getAuthHeader()) } }
    )

    if (ret.status === 200) {
      return ret.data
    } else throw new Error(ret.statusText)
  } catch (error) {
    logger.error(error.message)
    return null
  }
}

// export async function getNewPeople({ boardId, from }) {
//   try {
//     const ret = await axios.get(`${await API_URL()}/people/${boardId}/from/${from}`, {
//       headers: { ...(await getAuthHeader()) }
//     })
//
//     if (ret.status === 200) {
//       return ret.data
//     }
//     throw new Error(ret.statusText)
//   } catch (error) {
//     console.log(error)
//     return null
//   }
// }

export async function addPerson({ boardId, person }) {
  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/create`,
    { data: { person } },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 201) {
    return ret.data
  } else throw new Error(ret.statusText)
}

export async function updatePerson({ boardId, personId, person, historyMeta = null }) {
  const personData = { ...person }
  personData.removed = Boolean(person.removed)
  const data = { person: personData, ...(historyMeta ? { _historyMeta: historyMeta } : {}) }

  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/${personId}`,
    { data },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    return ret.data
  } else throw new Error(ret.statusText)
}

export async function updatePeople({ boardId, people }) {
  const ret = await axios.patch(
    `${await API_URL()}/people/${boardId}`,
    { data: { people } },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    return ret.data
  } else throw new Error(ret.statusText)
}

export async function shiftPerson({ boardId, personId, delta, historyMeta = null }) {
  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/${personId}/shift`,
    { data: { delta, ...(historyMeta ? { _historyMeta: historyMeta } : {}) } },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    return ret.data
  } else throw new Error(ret.statusText)
}

export async function updateManager({ boardId, personId, newManagerId, historyMeta = null }) {
  const data = { newManagerId, ...(historyMeta ? { _historyMeta: historyMeta } : {}) }

  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/${personId}/manager`,
    { data },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    return ret.data
  } else throw new Error(ret.statusText)
}

export async function updateManagerBulk({ boardId, peopleIds, newManagerId, historyMeta = null }) {
  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/bulk-manager`,
    { data: { peopleIds, newManagerId, ...(historyMeta ? { _historyMeta: historyMeta } : {}) } },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    return ret.data
  } else throw new Error(ret.statusText)
}

export async function performRootOperation({
  boardId,
  personId,
  action,
  lastManagers,
  historyMeta = null
}) {
  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/${personId}/root-operation`,
    { data: { action, lastManagers, ...(historyMeta ? { _historyMeta: historyMeta } : {}) } },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    return ret.data
  } else throw new Error(ret.statusText)
}

export async function deletePerson({ boardId, personId }) {
  const ret = await axios.delete(`${await API_URL()}/people/${boardId}/${personId}`, {
    headers: { ...(await getAuthHeader()) }
  })

  if (ret.status === 200) {
    // Return number of people deleted
    return ret.data
  } else throw new Error(ret.statusText)
}

export async function deletePeople({ boardId, personIds }) {
  const ret = await axios.delete(`${await API_URL()}/people/${boardId}`, {
    headers: { ...(await getAuthHeader()) },
    data: { personIds }
  })

  if (ret.status === 200) {
    // Return number of people deleted
    return ret.data
  } else throw new Error(ret.statusText)
}

/**
 * Deletes people with following side effects:
 * - managers of removed people will have their subordinates updates
 * - subordinates of removed people will have their managers updated to the next manager in the hierarchy
 */
export async function deletePeopleRetainHierarchy({ boardId, personIds }) {
  const ret = await axios.delete(`${await API_URL()}/people/${boardId}/retain-hierarchy`, {
    headers: { ...(await getAuthHeader()) },
    data: { personIds }
  })

  if (ret.status === 200) {
    // Return number of people deleted
    return ret.data
  } else throw new Error(ret.statusText)
}

export async function getPersonHistory({ boardId, personId, startIndex, max }) {
  try {
    const ret = await axios.get(
      `${await API_URL()}/people/${boardId}/${personId}/history/${startIndex}/${max}`,
      {
        headers: { ...(await getAuthHeader()) }
      }
    )

    if (ret.status === 200) {
      return ret.data
    }

    throw new Error(ret.statusText)
  } catch (error) {
    logger.error(error.message)
    return null
  }
}

//TODO: move to its own scope (reviews-api)
export async function submitPerson({ boardId, personId }) {
  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/${personId}/submit`,
    {},
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    return ret.data
  } else throw new Error(ret.statusText)
}

export async function bulkUpdate({
  boardId,
  peopleIds,
  properties,
  historyMeta = null,
  syncManagerSubordinates = false
}) {
  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/bulk`,
    {
      data: {
        peopleIds,
        properties,
        ...(historyMeta ? { _historyMeta: historyMeta } : {}),
        ...(syncManagerSubordinates ? { syncManagerSubordinates } : {})
      }
    },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    return ret.data.length
  } else throw new Error(ret.statusText)
}

export async function bulkStatusUpdate({
  boardId,
  peopleIds,
  status,
  effectiveDate,
  historyMeta = null
}) {
  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/bulk-status`,
    {
      data: {
        peopleIds,
        status,
        effectiveDate,
        ...(historyMeta ? { _historyMeta: historyMeta } : {})
      }
    },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    return ret.data.length
  } else throw new Error(ret.statusText)
}

export async function bulkStatusRemove({ boardId, peopleIds, status, historyMeta = null }) {
  const ret = await axios.delete(`${await API_URL()}/people/${boardId}/bulk-status`, {
    data: { peopleIds, status, ...(historyMeta ? { _historyMeta: historyMeta } : {}) },
    headers: { ...(await getAuthHeader()) }
  })

  if (ret.status === 200) {
    return ret.data.length
  } else throw new Error(ret.statusText)
}

const batchedRequestsForBoard = ref({})
const responses = ref({})
const functionForBoard = {}
const getRequestKey = (boardId, personId) => `${boardId}:${personId}`

const getFetchPeopleDebounced = (boardId) =>
  useDebounceFn(async () => {
    const personIds = [...new Set(batchedRequestsForBoard.value[boardId])]
    batchedRequestsForBoard.value[boardId] = []

    if (!personIds || personIds.length === 0) return

    try {
      const ret = await axios.post(
        `${await API_URL()}/people/${boardId}/get-batch`,
        { data: { personIds } },
        {
          headers: await getAuthHeader()
        }
      )

      if (ret.status === 200) {
        const data = decompressJson(ret.data) || []
        const result = keyBy(data, 'personId') || {}
        personIds.forEach((personId) => {
          const requestKey = getRequestKey(boardId, personId)
          responses.value[requestKey] = result[personId] || null
          // Resolve the promise for each board/person pair.
          if (pendingRequests.has(requestKey)) {
            pendingRequests.get(requestKey).resolve(responses.value[requestKey])
            pendingRequests.delete(requestKey)
          }
        })
      } else {
        throw new Error(ret.statusText)
      }
    } catch (error) {
      logger.error('Error fetching batched people data:', error.message)
      personIds.forEach((personId) => {
        const requestKey = getRequestKey(boardId, personId)
        responses.value[requestKey] = { error: error.message }
        // Reject the promise for each board/person pair.
        if (pendingRequests.has(requestKey)) {
          pendingRequests.get(requestKey).reject(error)
          pendingRequests.delete(requestKey)
        }
      })
    }
  }, 500) // Debounce interval of 500ms

async function addToQueue({ boardId, personId }) {
  const requestKey = getRequestKey(boardId, personId)
  if (pendingRequests.has(requestKey)) {
    // If a request is pending for this board/person pair, return its promise.
    return pendingRequests.get(requestKey).promise
  }

  // Create a new promise for this request
  let resolveFn, rejectFn
  const promise = new Promise((resolve, reject) => {
    resolveFn = resolve
    rejectFn = reject
  })

  // Add to pending requests for this board/person pair.
  pendingRequests.set(requestKey, { promise, resolve: resolveFn, reject: rejectFn })

  if (!batchedRequestsForBoard.value[boardId]) {
    batchedRequestsForBoard.value[boardId] = []
  }

  // Add personId to the queue if it's not already present
  if (!batchedRequestsForBoard.value[boardId].includes(personId)) {
    batchedRequestsForBoard.value[boardId].push(personId)
  }

  if (!functionForBoard[boardId]) {
    functionForBoard[boardId] = getFetchPeopleDebounced(boardId)
  }

  functionForBoard[boardId]()

  try {
    // Await the promise associated with this personId
    const result = await promise
    return result
  } catch (error) {
    logger.error('Error in addToQueue:', error.message)
    return { error: error.message }
  }
}

export const fetchPersonBatched = async ({ boardId, personId }) => {
  if (!boardId || !personId) return {}
  if (personId.includes('-collapsed-')) return {}

  try {
    const result = await addToQueue({ boardId, personId })

    if (result && result.error) {
      throw new Error(result.error)
    }

    return result || {}
  } catch (error) {
    logger.error('Error in fetchPersonBatched:', error.message)
    return { error: error.message }
  }
}

export const filter = async ({ boardId, filters }) => {
  const url = new URL(`${await API_URL()}/people/${boardId}/filter`)

  const ret = await axios.post(
    url,
    { data: { filters } },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    const data = decompressJson(ret.data) || []
    return data
  } else throw new Error(ret.statusText)
}

export const search = async ({
  boardId,
  searchQuery,
  state,
  includeEmployeeData = false,
  filters = []
}) => {
  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/search`,
    { data: { searchQuery, includeEmployeeData, state, filters } },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    return decompressJson(ret.data) || []
  } else throw new Error(ret.statusText)
}

/**
 * Search for positions by query. Returns positions matching the query or positions with an employee-data with a matching name.
 */
export const searchPosition = async ({ boardId, searchQuery, state, filters = [] }) => {
  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/search-position`,
    { data: { searchQuery, state, filters } },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    return decompressJson(ret.data) || []
  } else throw new Error(ret.statusText)
}

/**
 * Get sort values for a specific column (used for directory table sorting)
 * @param {Object} params
 * @param {string} params.boardId - The board ID
 * @param {string} params.column - The column ID to get sort values for
 * @param {Array} params.personIds - Optional array of personIds to get sort values for
 * @returns {Promise<Object>} - Map of { personId: sortValue }
 */
export const getSortValues = async ({ boardId, column, personIds }) => {
  const data = { column }
  if (Array.isArray(personIds)) data.personIds = personIds

  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/sort-values`,
    { data },
    { headers: { ...(await getAuthHeader()) } }
  )

  if (ret.status === 200) {
    const data = decompressJson(ret.data)
    return data?.values || {}
  } else throw new Error(ret.statusText)
}

export const exportCsv = async ({
  boardId,
  boardIds,
  filters = [],
  includeComparison = false,
  selectedFields,
  includeDetachedEmployees = false,
  attestedFields,
  attestedFieldId,
  csvType = 'export'
}) => {
  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/export-csv`,
    {
      data: {
        boardIds,
        filters,
        includeComparison,
        selectedFields,
        includeDetachedEmployees,
        attestedFields,
        attestedFieldId,
        csvType,
        exportDate: formatExportDate()
      }
    },
    { headers: { ...(await getAuthHeader()) }, responseType: 'arraybuffer' }
  )

  if (ret.status === 200) {
    const data = decompressBufferFflate(ret.data) || []
    return data
  } else if (ret.status === 202) {
    const data = JSON.parse(new TextDecoder().decode(ret.data))
    return { queued: true, ...data }
  } else {
    throw new Error(ret.statusText)
  }
}

/**
 * Export position data with Agentnoon ID as identifier and position-attributed custom fields only
 * @param {string} boardId - Board ID
 * @returns {Promise<string>} CSV data
 */
export const exportPositionData = async ({ boardId }) => {
  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/export-position-data`,
    { data: {} },
    { headers: { ...(await getAuthHeader()) }, responseType: 'arraybuffer' }
  )

  if (ret.status === 200) {
    const data = decompressBufferFflate(ret.data) || []
    return data
  } else throw new Error(ret.statusText)
}

/**
 * Export employee data with Employee ID as identifier and employee-attributed custom fields only
 * @param {string} boardId - Board ID
 * @returns {Promise<string>} CSV data
 */
export const exportEmployeeData = async ({ boardId }) => {
  const ret = await axios.post(
    `${await API_URL()}/people/${boardId}/export-employee-data`,
    { data: {} },
    { headers: { ...(await getAuthHeader()) }, responseType: 'arraybuffer' }
  )

  if (ret.status === 200) {
    const data = decompressBufferFflate(ret.data) || []
    return data
  } else throw new Error(ret.statusText)
}

// Cleanup function to remove old data and prevent memory leaks
function cleanup() {
  Object.keys(batchedRequestsForBoard.value).forEach((boardId) => {
    if (batchedRequestsForBoard.value[boardId].length === 0) {
      delete batchedRequestsForBoard.value[boardId]
      delete functionForBoard[boardId]
    }
  })
}

/**
 * Enhances theme colors with calculated values for PPT export
 * @param {Object} themeColors - Original theme colors from useTheme
 * @returns {Object} Enhanced theme colors with bg, ring, and textColor properties
 */
function enhanceThemeColors(themeColors) {
  if (!themeColors) return null

  // Create a deep copy of the theme colors
  const enhancedTheme = JSON.parse(JSON.stringify(themeColors))

  // Process each category (departments, locations, roles, etc.)
  Object.keys(enhancedTheme).forEach((category) => {
    if (typeof enhancedTheme[category] === 'object') {
      // Process each item in the category
      Object.keys(enhancedTheme[category]).forEach((key) => {
        const baseColor = enhancedTheme[category][key]
        // Skip if not a valid color
        if (!baseColor || typeof baseColor !== 'string') return

        try {
          // Follow the same process as UI rendering:
          // 1. First calculate mid-luminance color (like pillColor in UI)
          const midLuminanceColor = chroma(baseColor).luminance(0.6).hex()

          // 2. Then calculate final background from the mid-luminance color
          const bgColor = chroma(midLuminanceColor).luminance(0.9).hex()

          // Replace the color string with an object containing calculated values
          enhancedTheme[category][key] = {
            base: baseColor,
            bg: bgColor,
            ring: midLuminanceColor, // Use mid-luminance color for ring
            textColor: chroma(midLuminanceColor).luminance() < 0.5 ? '#ffffff' : '#000000'
          }
        } catch (error) {
          logger.warn(`Invalid color format for ${category}.${key}: ${baseColor}`)
          // Provide fallback values if color parsing fails
          enhancedTheme[category][key] = {
            base: baseColor,
            bg: '#ffffff',
            ring: '#d0d5dd',
            textColor: '#000000'
          }
        }
      })
    }
  })

  return enhancedTheme
}

/**
 * Get the highest position ID for a board
 * @param {Object} params
 * @param {string} params.boardId - The board ID
 * @returns {Promise<string>} - The highest position ID
 */
export const getHighestPositionId = async ({ boardId }) => {
  const ret = await axios.get(`${await API_URL()}/people/${boardId}/highest-position-id`, {
    headers: { ...(await getAuthHeader()) }
  })

  if (ret.status === 200) {
    return ret.data.displayedPositionId
  } else throw new Error(ret.statusText)
}

export const exportDuplicatedPositionIds = async (boardId) => {
  const ret = await axios.get(
    `${await API_URL()}/people/export-duplicated-position-ids/${boardId}`,
    {
      headers: { ...(await getAuthHeader()) },
      responseType: 'blob'
    }
  )

  if (ret.status === 200) {
    return ret.data
  } else throw new Error(ret.statusText)
}

// Run cleanup periodically
setInterval(cleanup, 60000) // Cleanup interval of 1 minute
